import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Matrix } from "@babylonjs/core/Maths/math.vector";
import { COMBAT_ART } from "./generatedCombatArt.ts";
import { MUSSOLINI_FX } from "./generatedMussoliniArt.ts";
import { GANDHI_FX } from "./generatedGandhiArt.ts";

/**
 * Effects of the moves, drawn as pixel-art frame sheets (docs/STANDARD_SPRITE.md,
 * "Altri file"): a sheet is a grid of frames, played once where the move
 * happens, sized to the move's real area. "ground" effects lie flat on the
 * floor (rings, shockwaves), seen from the camera at an angle like the arena.
 */
export interface EffectSheet {
  /** Under /proto2d/fx/. */
  readonly file: string;
  readonly cols: number;
  readonly rows: number;
  readonly frames: number;
  readonly fps: number;
  /** Share of the frame the drawing fills (the art's edge), to size it to the area. */
  readonly fill: number;
  readonly linger?: number;
  readonly aspect?: number;
  readonly pivotX?: number;
  readonly pivotY?: number;
}

/** Effect sheets by name. */
const SHEETS: Record<string, EffectSheet> = {
  "dante-inferno": { file: "dante-inferno.png", cols: 3, rows: 3, frames: 9, fps: 14, fill: 0.95 },
  "dante-inferno-v2": { file: "../dante-v2/inferno.png", cols: 5, rows: 4, frames: 18, fps: 15, fill: 0.95, linger: 2.5 },
  "dante-pages": { file: "../dante-v2/pages.png", cols: 5, rows: 2, frames: 8, fps: 16, fill: 0.95 },
  "dante-slash": { file: "../dante-v2/slash.png", cols: 5, rows: 2, frames: 6, fps: 17, fill: 0.95 },
  // Maria Antonietta's brioche going off: cream, powdered sugar and pink hearts (D46).
  "antonietta-sugar": { file: "../antonietta/sugar.png", cols: 4, rows: 1, frames: 4, fps: 12, fill: 0.95 },
  ...COMBAT_ART,
  ...GANDHI_FX,
  ...MUSSOLINI_FX,
};

/** Which effect a champion's move shows: champion id → move → sheet name. */
const CHAMPION_EFFECTS: Record<string, Partial<Record<"super", string>>> = {
  dante: { super: "dante-inferno-v2" },

};

export function effectFor(championId: string, move: "super"): string | undefined {
  return CHAMPION_EFFECTS[championId]?.[move];
}

interface Playing {
  flip: boolean;
  key?: string;
  mesh: Mesh;
  material: StandardMaterial;
  texture: Texture;
  sheet: EffectSheet;
  age: number;
  size: number;
}

export class MoveEffects {
  private readonly scene: Scene;
  private readonly playing: Playing[] = [];

  constructor(scene: Scene) {
    this.scene = scene;
  }

  /**
   * A ground effect centred at (x, y, z), covering a circle of `radius`
   * metres: it grows in quickly, plays its frames once, and fades at the end.
   */
  ground(name: string, x: number, y: number, z: number, radius: number, key?: string): boolean {
    return this.play(name, x, y, z, radius, false, key);
  }

  /** Small airborne effects face the camera rather than lying on the floor. */
  burst(name: string, x: number, y: number, z: number, radius: number, flip = false): boolean {
    return this.play(name, x, y, z, radius, true, undefined, flip);
  }

  private play(name: string, x: number, y: number, z: number, radius: number, upright: boolean, key?: string, flip = false): boolean {
    const sheet = SHEETS[name];
    if (!sheet) return false;
    const mesh = CreatePlane(`fx-${name}`, { size: 1 }, this.scene);
    const px=sheet.pivotX??0.5,py=sheet.pivotY??0.5;
    mesh.bakeTransformIntoVertices(Matrix.Translation((0.5-px)*(flip?-1:1),py-0.5,0));
    if (upright) mesh.billboardMode = Mesh.BILLBOARDMODE_ALL;
    else mesh.rotation.x = Math.PI / 2;
    mesh.position.set(x, y + (upright ? 0 : 0.06), z);
    mesh.isPickable = false;
    const texture = new Texture(`/proto2d/fx/${sheet.file}`, this.scene, true, true, Texture.NEAREST_SAMPLINGMODE);
    texture.hasAlpha = true;
    texture.wrapU = Texture.CLAMP_ADDRESSMODE;
    texture.wrapV = Texture.CLAMP_ADDRESSMODE;
    texture.uScale = (flip ? -1 : 1) / sheet.cols;
    texture.vScale = 1 / sheet.rows;
    const material = new StandardMaterial(`fx-${name}`, this.scene);
    material.diffuseTexture = texture;
    material.useAlphaFromDiffuseTexture = true;
    material.emissiveColor = new Color3(1, 1, 1);
    material.disableLighting = true;
    material.backFaceCulling = false;
    mesh.material = material;
    const size = (radius * 2) / sheet.fill;
    this.playing.push({ mesh, material, texture, sheet, age: 0, size, key, flip });
    this.showFrame(this.playing[this.playing.length - 1]!, 0);
    return true;
  }

  cancel(key: string): void {
    for (let i=this.playing.length-1;i>=0;i--) {
      if (this.playing[i]!.key !== key) continue;
      const p = this.playing[i]!;
      p.mesh.dispose(); p.material.dispose(); p.texture.dispose();
      this.playing.splice(i,1);
    }
  }

  clear(): void {
    for (const p of this.playing) { p.mesh.dispose(); p.material.dispose(); p.texture.dispose(); }
    this.playing.length=0;
  }

  update(dt: number): void {
    for (let i = this.playing.length - 1; i >= 0; i--) {
      const p = this.playing[i]!;
      p.age += dt;
      const duration = p.sheet.frames / p.sheet.fps;
      if (p.age >= duration + (p.sheet.linger ?? 0)) {
        p.mesh.dispose();
        p.material.dispose();
        p.texture.dispose();
        this.playing.splice(i, 1);
        continue;
      }
      if (p.age >= duration && p.sheet.linger) {
        this.showFrame(p, 15);
        p.material.emissiveColor.set(0.08, 0.04, 0.04);
        p.material.alpha = 0.4 * Math.min(1, (duration + p.sheet.linger - p.age) / 0.6);
        continue;
      }
      this.showFrame(p, Math.min(p.sheet.frames - 1, Math.floor(p.age * p.sheet.fps)));
      // Grows in over the first 0.1 s, fades over the last fifth.
      const grow = Math.min(1, 0.7 + (p.age / 0.1) * 0.3);
      p.mesh.scaling.set(p.size * grow, p.size * grow / (p.sheet.aspect ?? 1), 1);
      const left = duration - p.age;
      p.material.alpha = Math.min(1, left / (duration * 0.2));
    }
  }

  private showFrame(p: Playing, frame: number): void {
    const col = frame % p.sheet.cols;
    const row = Math.floor(frame / p.sheet.cols);
    p.texture.uOffset = (col + (p.flip ? 1 : 0)) / p.sheet.cols;
    p.texture.vOffset = 1 - (row + 1) / p.sheet.rows;
  }
}
